import { useId, useState, type ChangeEvent, type FormEvent } from 'react'
import { Check, Loader2, Paperclip, X } from 'lucide-react'
import { api } from '@/lib/api'
import {
  ACCEPT_ATTACHMENT_ATTR,
  isAllowedAttachmentFile,
  MAX_ATTACHMENT_BYTES,
} from '@/lib/attachmentRules'
import { buildFeedbackClientContext } from '@/lib/feedbackClientContext'
import { formatUsPhoneE164, isValidUsPhone } from '@/lib/usPhone'
import { storeForm } from '@/components/store/storeFormStyles'

const MIN_DESC = 10

function isValidEmail(s: string): boolean {
  const t = s.trim()
  if (!t) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)
}

export default function RequestNewItemForm() {
  const attachmentInputId = useId()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [brandName, setBrandName] = useState('')
  const [itemName, setItemName] = useState('')
  const [productDescription, setProductDescription] = useState('')
  const [attachment, setAttachment] = useState<File | null>(null)
  const [attachmentFileKey, setAttachmentFileKey] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const hasEmail = email.trim().length > 0
  const hasPhone = phone.trim().length > 0
  const contactOk = hasEmail || hasPhone
  const emailOk = !hasEmail || isValidEmail(email)
  const phoneOk = !hasPhone || isValidUsPhone(phone)
  const descLen = productDescription.trim().length
  const descOk = descLen >= MIN_DESC

  const canSubmit =
    name.trim().length > 0 &&
    brandName.trim().length > 0 &&
    itemName.trim().length > 0 &&
    descOk &&
    contactOk &&
    emailOk &&
    phoneOk &&
    !submitting

  function onAttachmentChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) {
      setAttachment(null)
      return
    }
    if (f.size > MAX_ATTACHMENT_BYTES) {
      setAttachment(null)
      setAttachmentFileKey((k) => k + 1)
      setError(`Attachment must be ${MAX_ATTACHMENT_BYTES / 1024} KB or smaller.`)
      return
    }
    if (!isAllowedAttachmentFile(f)) {
      setAttachment(null)
      setAttachmentFileKey((k) => k + 1)
      return
    }
    setAttachment(f)
    setError(null)
  }

  function clearAttachment() {
    setAttachment(null)
    setAttachmentFileKey((k) => k + 1)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError('Please enter your name.')
      return
    }
    if (!contactOk) {
      setError('Please add an email or US phone number (or both).')
      return
    }
    if (!emailOk) {
      setError('Please enter a valid email address.')
      return
    }
    if (!phoneOk) {
      setError('Please enter a valid US phone number, or clear the phone field.')
      return
    }
    if (!brandName.trim() || !itemName.trim() || !descOk) {
      setError('Please complete brand name, item name, and description (at least 10 characters).')
      return
    }
    setSubmitting(true)
    try {
      const client_context = await buildFeedbackClientContext()
      const res = await api.storefront.submitItemRequest(
        {
          requester_name: name.trim(),
          brand_name: brandName.trim(),
          item_name: itemName.trim(),
          description: productDescription.trim(),
          client_context,
          ...(hasEmail ? { email: email.trim() } : {}),
          ...(hasPhone ? { phone: formatUsPhoneE164(phone) } : {}),
        },
        attachment
      )
      if (res.success) {
        setSubmitted(true)
      } else {
        setError('We could not save your request. Please try again.')
      }
      setAttachment(null)
      setAttachmentFileKey((k) => k + 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div
        className={`${storeForm.body} text-center`}
        role="status"
        aria-live="polite"
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
          <Check className="h-6 w-6" strokeWidth={2.5} aria-hidden />
        </div>
        <h2 id="request-item-title" className="mt-3 text-base font-semibold text-stone-900 sm:text-lg">Request received</h2>
        <p className="mx-auto mt-1.5 max-w-md text-xs leading-relaxed text-stone-600 sm:text-sm">
          Thanks — we&apos;ll review your new item request and do our best to stock it. We may reach out using the
          contact you provided.
        </p>
      </div>
    )
  }

  return (
    <div className={storeForm.body}>
      <h2 id="request-item-title" className={storeForm.title}>Request an item</h2>
      <p className={storeForm.intro}>Tell us what you&apos;d like us to carry. Fields marked * are required.</p>

      <form onSubmit={handleSubmit} className={storeForm.form} noValidate>
        <div className="space-y-3">
          <h3 className={storeForm.sectionLabel}>Your details</h3>
          <div className="space-y-1">
            <label htmlFor="req-name" className={storeForm.fieldLabel}>
              Name <span className="text-red-600">*</span>
            </label>
            <input
              id="req-name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={`${storeForm.input} ${storeForm.inputOk}`}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
            <div className="space-y-1">
              <label htmlFor="req-email" className={storeForm.fieldLabel}>
                Email {!hasPhone ? <span className="text-red-600">*</span> : null}
              </label>
              <input
                id="req-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={hasEmail && !emailOk}
                className={`${storeForm.input} ${hasEmail && !emailOk ? storeForm.inputBad : storeForm.inputOk}`}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="req-phone" className={storeForm.fieldLabel}>
                Phone (US) {!hasEmail ? <span className="text-red-600">*</span> : null}
              </label>
              <input
                id="req-phone"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. (312) 588-2300"
                aria-invalid={hasPhone && !phoneOk}
                className={`${storeForm.input} ${hasPhone && !phoneOk ? storeForm.inputBad : storeForm.inputOk}`}
              />
            </div>
          </div>
          <p className={storeForm.hint}>Add at least one: email or phone (both is fine).</p>
        </div>

        <div className="space-y-3 border-t border-stone-100 pt-4 sm:pt-6">
          <h3 className={storeForm.sectionLabel}>Product details</h3>
          <div className="space-y-1">
            <label htmlFor="req-brand" className={storeForm.fieldLabel}>
              Brand name <span className="text-red-600">*</span>
            </label>
            <input
              id="req-brand"
              type="text"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              required
              className={`${storeForm.input} ${storeForm.inputOk}`}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="req-item" className={storeForm.fieldLabel}>
              Item name <span className="text-red-600">*</span>
            </label>
            <input
              id="req-item"
              type="text"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              required
              className={`${storeForm.input} ${storeForm.inputOk}`}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="req-desc" className={storeForm.fieldLabel}>
              Description <span className="text-red-600">*</span>
            </label>
            <textarea
              id="req-desc"
              value={productDescription}
              onChange={(e) => setProductDescription(e.target.value)}
              rows={4}
              maxLength={5000}
              required
              placeholder="Size, flavor, where you usually buy it, or anything that helps us find it."
              className={`resize-y ${storeForm.input} ${storeForm.inputOk}`}
            />
            <div className={`flex justify-between ${storeForm.hint}`}>
              <span>
                {descLen < MIN_DESC ? `${MIN_DESC - descLen} more characters needed` : 'Looks good'}
              </span>
              <span className="tabular-nums">{descLen} / 5000</span>
            </div>
          </div>
        </div>

        <div className="space-y-1.5 border-t border-stone-100 pt-4 sm:space-y-2 sm:pt-6">
          <label htmlFor={attachmentInputId} className={`flex items-center gap-1.5 ${storeForm.fieldLabel}`}>
            <Paperclip className="h-3.5 w-3.5 text-stone-500" aria-hidden />
            Attachment (optional)
          </label>
          <p className={storeForm.hint}>
            One file, up to 500 KB — JPG, PNG, or PDF only. Other types are ignored.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              key={attachmentFileKey}
              id={attachmentInputId}
              type="file"
              accept={ACCEPT_ATTACHMENT_ATTR}
              onChange={onAttachmentChange}
              className={storeForm.file}
            />
            {attachment ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-1 text-xs text-stone-700">
                <span className="max-w-[200px] truncate">{attachment.name}</span>
                <button
                  type="button"
                  onClick={clearAttachment}
                  className="rounded p-0.5 text-stone-500 hover:bg-stone-200 hover:text-stone-800"
                  aria-label="Remove attachment"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ) : null}
          </div>
        </div>

        {error ? (
          <p className={storeForm.error} role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={!canSubmit}
          className={storeForm.submit}
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              Sending…
            </>
          ) : (
            'Submit request'
          )}
        </button>
      </form>
    </div>
  )
}
